import React, { useState } from "react";
import { useApp, api } from "@/context/AppProvider";
import { Reveal, Magnetic } from "@/components/motion";
import { EditableText } from "@/components/editable";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CONTACT } from "@/content";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight } from "lucide-react";

const inputStyle =
  "bg-transparent border-0 border-b rounded-none px-0 h-12 text-base focus-visible:ring-0 focus-visible:border-b-2 shadow-none";

export const Contact = () => {
  const { tr, lang } = useApp();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(tr.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(tr.contact.error);
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: Mail, label: tr.contact.emailLabel, value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Phone, label: tr.contact.phoneLabel, value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, ltr: true },
    { icon: Linkedin, label: tr.contact.linkedinLabel, value: "linkedin.com/in/ali-inkis", href: CONTACT.linkedin, ltr: true },
    { icon: MapPin, label: tr.contact.locationLabel, value: lang === "ar" ? CONTACT.location_ar : CONTACT.location_en },
  ];

  return (
    <section id="contact" data-testid="contact" className="relative py-24 md:py-32 lg:py-40" style={{ backgroundColor: "var(--sand)" }}>
      <div className="container-lux grid lg:grid-cols-2 gap-16">
        <Reveal>
          <EditableText as="p" path={["contact", "eyebrow"]} value={tr.contact.eyebrow} className="eyebrow mb-4 inline-block" />
          <EditableText as="h2" path={["contact", "title"]} value={tr.contact.title} className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6 block" style={{ color: "var(--ink)" }} />
          <EditableText as="p" multiline path={["contact", "subtitle"]} value={tr.contact.subtitle} className="font-body text-lg leading-relaxed mb-12 max-w-md block" style={{ color: "var(--ink-soft)" }} />

          <div className="space-y-6">
            {details.map((d, i) => {
              const Icon = d.icon;
              const content = (
                <div className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-colors" style={{ backgroundColor: "rgba(197,160,89,0.12)" }}>
                    <Icon size={18} style={{ color: "var(--gold)" }} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.25em]" style={{ color: "var(--ink-soft)" }}>{d.label}</p>
                    <p className="font-body text-base" dir={d.ltr ? "ltr" : undefined} style={{ color: "var(--ink)" }}>{d.value}</p>
                  </div>
                </div>
              );
              return d.href ? (
                <a key={i} href={d.href} target={d.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-${d.label}`}>{content}</a>
              ) : (
                <div key={i}>{content}</div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="rounded-2xl p-8 md:p-10 shadow-soft" style={{ backgroundColor: "var(--ivory)", border: "1px solid rgba(19,19,19,0.06)" }} data-testid="contact-form">
            <div className="space-y-7">
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.contact.name}</Label>
                <Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputStyle} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.contact.email}</Label>
                <Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputStyle} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.contact.subject}</Label>
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputStyle} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.contact.message}</Label>
                <Textarea data-testid="contact-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-transparent border-0 border-b rounded-none px-0 text-base focus-visible:ring-0 focus-visible:border-b-2 shadow-none resize-none" />
              </div>
              <Magnetic className="w-full">
                <button data-testid="contact-submit" type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-white text-sm font-body font-medium disabled:opacity-60" style={{ backgroundColor: "var(--ink)" }}>
                  {sending ? tr.contact.sending : tr.contact.send}
                  {!sending && <ArrowUpRight size={17} />}
                </button>
              </Magnetic>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
